/**
 * Reddit Conversions API utility functions for frontend
 * Used for tracking user actions and conversions
 */

import { REDDIT_PIXEL_CONFIG } from '@/config/reddit'
import { ConversionIdGenerator, getUserSessionId } from './conversion-id-generator'

declare global {
  interface Window {
    rdt: (action: string, ...args: any[]) => void
  }
}

/**
 * Track Reddit conversion event
 */
export function trackRedditConversion(eventType: string, customData?: Record<string, any>) {
  if (typeof window !== 'undefined' && window.rdt) {
    try {
      window.rdt('track', eventType, customData)
      console.log('📊 Reddit conversion tracked:', eventType, customData)
    } catch (error) {
      console.error('❌ Failed to track Reddit conversion:', error)
    }
  } else {
    console.warn('Reddit pixel not loaded')
  }
}

/**
 * Track Reddit conversion event with conversion ID
 * This is the recommended format for Reddit conversion tracking
 */
export function trackRedditConversionWithId(conversionEvent: string, conversionId: string, customData?: Record<string, any>) {
  if (typeof window !== 'undefined' && window.rdt) {
    try {
      const payload = {
        conversionId: conversionId,
        ...customData
      }
      window.rdt('track', conversionEvent, payload)
      console.log('📊 Reddit conversion tracked with ID:', conversionEvent, payload)
    } catch (error) {
      console.error('❌ Failed to track Reddit conversion with ID:', error)
    }
  } else {
    console.warn('Reddit pixel not loaded')
  }
}

/**
 * Track page view
 */
export function trackRedditPageView(pageName?: string) {
  trackRedditConversion('PageVisit', {
    page_name: pageName || window.location.pathname,
    timestamp: Date.now()
  })
}

/**
 * Track registration
 */
export function trackRedditRegistration(userData?: Record<string, any>) {
  const userId = userData?.userId || 'anonymous'
  const conversionId = ConversionIdGenerator.registration(userId, getUserSessionId())
  
  trackRedditConversionWithId('CompleteRegistration', conversionId, {
    value: REDDIT_PIXEL_CONFIG.DEFAULT_VALUES.REGISTRATION,
    currency: REDDIT_PIXEL_CONFIG.DEFAULT_CURRENCY,
    content_type: 'registration',
    status: 'completed',
    conversion_id: conversionId, // Include in custom data for API sync
    ...userData
  })
}

/**
 * Track RFP creation
 */
export function trackRedditRfpCreation(rfpData?: Record<string, any>) {
  const userId = rfpData?.userId || 'anonymous'
  const rfpId = rfpData?.rfpId || 'unknown'
  const conversionId = ConversionIdGenerator.rfpCreation(userId, rfpId, getUserSessionId())
  
  trackRedditConversionWithId('SubmitApplication', conversionId, {
    value: REDDIT_PIXEL_CONFIG.DEFAULT_VALUES.RFP_CREATION,
    currency: REDDIT_PIXEL_CONFIG.DEFAULT_CURRENCY,
    content_type: 'rfp',
    content_name: rfpData?.title || 'RFP Creation',
    content_category: rfpData?.category || 'business',
    status: 'completed',
    conversion_id: conversionId, // Include in custom data for API sync
    ...rfpData
  })
}

/**
 * Track subscription upgrade
 */
export function trackRedditSubscriptionUpgrade(subscriptionData?: Record<string, any>) {
  const userId = subscriptionData?.userId || 'anonymous'
  const planId = subscriptionData?.planId || 'unknown'
  const conversionId = ConversionIdGenerator.subscription(userId, planId, getUserSessionId())
  
  trackRedditConversionWithId('Purchase', conversionId, {
    value: subscriptionData?.amount || REDDIT_PIXEL_CONFIG.DEFAULT_VALUES.SUBSCRIPTION,
    currency: subscriptionData?.currency || REDDIT_PIXEL_CONFIG.DEFAULT_CURRENCY,
    content_type: 'subscription',
    content_name: subscriptionData?.plan || 'Pro Plan',
    content_category: 'business',
    num_items: 1,
    status: 'completed',
    conversion_id: conversionId, // Include in custom data for API sync
    ...subscriptionData
  })
}

/**
 * Track contact form submission
 */
export function trackRedditContactForm(subject?: string, additionalData?: Record<string, any>) {
  const userId = additionalData?.userId || 'anonymous'
  const conversionId = ConversionIdGenerator.contact(userId, subject || 'Contact Form', getUserSessionId())
  
  trackRedditConversionWithId('Contact', conversionId, {
    value: REDDIT_PIXEL_CONFIG.DEFAULT_VALUES.CONTACT_FORM,
    currency: REDDIT_PIXEL_CONFIG.DEFAULT_CURRENCY,
    content_type: 'contact',
    content_name: subject || 'Contact Form',
    content_category: 'inquiry',
    status: 'completed',
    conversion_id: conversionId, // Include in custom data for API sync
    ...additionalData
  })
}

/**
 * Track button click
 */
export function trackRedditButtonClick(buttonName: string, additionalData?: Record<string, any>) {
  trackRedditConversion('ButtonClick', {
    button_name: buttonName,
    page_url: window.location.href,
    timestamp: Date.now(),
    ...additionalData
  })
}

/**
 * Track search
 */
export function trackRedditSearch(searchTerm: string, resultsCount?: number) {
  trackRedditConversion('Search', {
    search_string: searchTerm,
    num_results: resultsCount || 0,
    timestamp: Date.now()
  })
}

/**
 * Initialize Reddit pixel with advanced matching (if not already initialized)
 */
export function initializeRedditPixel(pixelId: string, userIdentifiers?: {
  email?: string
  phoneNumber?: string
  externalId?: string
  idfa?: string
  aaid?: string
}) {
  if (typeof window !== 'undefined' && !window.rdt) {
    try {
      // Load Reddit pixel script
      const script = document.createElement('script')
      
      // Build init call with or without advanced matching
      const initCall = userIdentifiers 
        ? `rdt('init', '${pixelId}', ${JSON.stringify(userIdentifiers)});`
        : `rdt('init', '${pixelId}');`
      
      script.innerHTML = `
        !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);
        ${initCall}
        rdt('track','PageVisit');
      `
      document.head.appendChild(script)
      console.log('📊 Reddit pixel initialized with ID:', pixelId)
      if (userIdentifiers) {
        console.log('📊 Advanced matching enabled:', Object.keys(userIdentifiers))
      }
    } catch (error) {
      console.error('❌ Failed to initialize Reddit pixel:', error)
    }
  }
}

/**
 * Update Reddit pixel with user identifiers for advanced matching
 * Call this when user logs in or when you have user information
 */
export function updateRedditUserIdentifiers(userIdentifiers: {
  email?: string
  phoneNumber?: string
  externalId?: string
  idfa?: string
  aaid?: string
}) {
  if (typeof window !== 'undefined' && window.rdt) {
    try {
      window.rdt('init', REDDIT_PIXEL_CONFIG.PIXEL_ID, userIdentifiers)
      console.log('📊 Reddit user identifiers updated:', Object.keys(userIdentifiers))
    } catch (error) {
      console.error('❌ Failed to update Reddit user identifiers:', error)
    }
  }
}
